import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Play, Pause, Music4 } from "lucide-react";

export default function SessionPlayers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/players").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16" data-testid="players-page">
      <div className="mono-caps mb-3">Enstrüman Kayıt Servisi</div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-3xl">
        Şarkına <span className="gradient-text">enstrüman çaldıralım</span>.
      </h1>
      <p className="mt-4 text-slate-400 max-w-2xl">
        Session müzisyenlerimizin çaldığı örnekleri dinle. Her enstrümanist için sabit stüdyo ücreti
        aşağıda listelenmiştir. Sipariş vermek için stüdyoyla iletişime geçin.
      </p>

      {loading && <div className="text-slate-500 mt-10">Yükleniyor…</div>}

      {!loading && items.length === 0 && (
        <div className="panel p-10 mt-10 text-center">
          <div className="mono-caps text-slate-400">Henüz enstrümanist yok</div>
          <div className="font-display text-xl mt-2 text-white">
            Admin panelinden ilk session müzisyenlerinizi ve örnek kayıtlarını ekleyin.
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        {items.map((p) => (
          <PlayerCard key={p.player_id} player={p} />
        ))}
      </div>
    </main>
  );
}

function PlayerCard({ player }) {
  const [current, setCurrent] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!audioRef.current) return;
    if (current) {
      audioRef.current.src = fileUrl(current.storage_path);
      audioRef.current.play().catch(() => setCurrent(null));
    } else {
      audioRef.current.pause();
    }
  }, [current]);

  return (
    <div className="panel p-6 panel-hover fade-up" data-testid={`player-${player.player_id}`}>
      <div className="flex items-start gap-4">
        {player.photo_path ? (
          <img src={fileUrl(player.photo_path)} alt="" className="w-20 h-20 rounded-lg object-cover border border-white/5" />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
            <Music4 className="w-8 h-8 text-cyan-300" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="mono-caps text-amber-400">{player.instrument}</div>
          <h3 className="font-display text-2xl text-white mt-1 truncate">{player.name}</h3>
          {player.description && <p className="text-sm text-slate-400 mt-2">{player.description}</p>}
        </div>
        <div className="text-right">
          <div className="mono-caps">Şarkı Başına</div>
          <div className="font-display text-2xl text-white">
            {Number(player.price).toLocaleString("tr-TR")} <span className="text-sm text-slate-400">₺</span>
          </div>
        </div>
      </div>

      {player.samples?.length > 0 && (
        <div className="mt-5 space-y-2" data-testid={`samples-${player.player_id}`}>
          <div className="mono-caps">Örnek Kayıtlar</div>
          {player.samples.map((s) => {
            const isPlaying = current?.sample_id === s.sample_id;
            return (
              <button
                key={s.sample_id}
                onClick={() => setCurrent(isPlaying ? null : s)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-white/5 hover:border-cyan-400/40 transition text-left"
                data-testid={`sample-play-${s.sample_id}`}
              >
                <span className="w-9 h-9 rounded-full bg-cyan-400 text-black flex items-center justify-center flex-shrink-0">
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                </span>
                <span className="text-white">{s.title}</span>
                {isPlaying && (
                  <span className="ml-auto flex items-end gap-[2px] h-4">
                    {[6, 12, 8, 14, 10].map((h, i) => (
                      <span key={i} className="waveform-bar" style={{ height: h, animationDelay: `${i * 0.1}s` }} />
                    ))}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <audio ref={audioRef} preload="none" onEnded={() => setCurrent(null)} />
    </div>
  );
}
